/**
 * vialogosService.ts
 *
 * Service for sending feedback, bug reports, and error reports to ViaLogos API
 */

const VIALOGOS_API_URL = 'https://api.cynner-g.ca/api/messages';

export type ReportSeverity = 'info' | 'error' | 'request' | 'idea';
export type ReportType = 'help' | 'bug' | 'idea' | 'error';

/**
 * Get the severity level based on report type
 */
function getSeverityForType(type: ReportType): ReportSeverity {
  switch (type) {
    case 'error':
      return 'error';
    case 'bug':
      return 'error';
    case 'idea':
      return 'idea';
    case 'help':
    default:
      return 'request';
  }
}

/**
 * Get the title prefix for the message subject
 */
function getTitlePrefix(type: ReportType): string {
  switch (type) {
    case 'error':
      return 'ERROR:';
    case 'bug':
      return 'BUG:';
    case 'idea':
      return 'IDEA:';
    case 'help':
    default:
      return 'HELP:';
  }
}

/**
 * Send a report to DreamCatcher
 */
export interface SendReportOptions {
  type: ReportType;
  message: string;
  email?: string;
  location?: string;
  stackTrace?: string;
}

export async function sendReport(options: SendReportOptions): Promise<{ success: boolean; error?: string }> {
  const { type, message, email, location, stackTrace } = options;

  // Build the message content
  let reportContent = message;

  if (location) {
    reportContent += `\n\nLocation: ${location}`;
  }

  if (stackTrace) {
    reportContent += `\n\nStack Trace:\n${stackTrace}`;
  }

  // For internal errors, use errors@vialogos as the email
  const reportEmail = type === 'error' && !email ? 'errors@vialogos' : email;

  // Build the payload
  const payload = {
    application: 'ViaLogos',
    severity: getSeverityForType(type),
    message: `${getTitlePrefix(type)} ${reportContent}`,
    email: reportEmail || undefined,
  };

  try {
    const response = await fetch(VIALOGOS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ViaLogos API error:', errorText);
      return { success: false, error: `Failed to send report: ${response.status}` };
    }

    const result = await response.json();
    return { success: result.success === true };
  } catch (error) {
    console.error('Error sending report to ViaLogos:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

/**
 * Send an error report automatically (for caught exceptions)
 */
export async function reportError(
  error: Error,
  location: string,
  additionalInfo?: string
): Promise<void> {
  const stackTrace = error.stack || 'No stack trace available';
  const message = additionalInfo
    ? `${additionalInfo}\n\n${error.message}`
    : error.message;

  await sendReport({
    type: 'error',
    message,
    location,
    stackTrace,
  });
}

/**
 * Wrap an async function with error reporting
 */
export function withErrorReporting<T extends (...args: any[]) => Promise<any>>(
  location: string,
  fn: T
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (error instanceof Error) {
        await reportError(error, location);
      } else {
        await sendReport({
          type: 'error',
          message: String(error),
          location,
        });
      }
      throw error; // Re-throw to maintain expected behavior
    }
  }) as T;
}
